import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  CLUSTER_META,
  clusterFromRoute,
  openExternalCabin,
  presentationOf,
} from "@/entities/capability";
import type { CapabilityCard as CapabilityCardData } from "@/entities/capability";
import { InFlightRow } from "@/entities/work/ui/InFlightRow";
import { RecentWorksRow } from "@/entities/work/ui/RecentWorksRow";
import type { WorkLineKey } from "@/entities/work/model/types";

import { WorkbenchDeptCard } from "./WorkbenchDeptCard";
import { WorkbenchExternalCabinCard } from "./WorkbenchExternalCabinCard";
import { CapabilityCard } from "./CapabilityCard";
import { useClusterCards } from "./useClusterCards";

/** dept moduleKey → 作品线(只有会出产物的三舱有;追爆无独立线)。用于群页的在途/最近。 */
const LINE_OF: Record<string, WorkLineKey> = {
  ai_poster: "poster",
  ai_video: "video",
  ai_digital_human: "dh",
};

/**
 * 群页(Cluster Hub)—— **发现流的工作层**(2026-07-25 改造):点大类落到这里。
 *
 * 只聚焦一个群:该群完整卡墙(内容群走富部门卡 `.dept-grid`,其余群走紧凑能力卡 `.cardwall-cap`)
 * + 该群在途/最近(仅内容群有产物线)。归组/门控/计数全走 `useClusterCards` 单源,与首页横滑带一致。
 * 首页「查看全部 →」/ 轨群图标都导到 `/g/:cluster`。非法 key → 回首页。
 */
export default function ClusterPage() {
  const { cluster: raw } = useParams();
  const cluster = clusterFromRoute(raw);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { forCluster } = useClusterCards();

  if (!cluster) return <Navigate to="/" replace />;

  const { depts, caps, counts } = forCluster(cluster);
  const meta = CLUSTER_META[cluster];

  const countParts: string[] = [];
  if (counts.active > 0) countParts.push(`${counts.active} ${t("cabin.status.active")}`);
  if (counts.integrating > 0)
    countParts.push(`${counts.integrating} ${t("cabin.status.integrating")}`);
  if (counts.coming > 0) countParts.push(`${counts.coming} ${t("cabin.status.coming")}`);

  const workLines = depts
    .map((d) => LINE_OF[d.moduleKey])
    .filter((l): l is WorkLineKey => Boolean(l));

  const isEmpty = depts.length + caps.length === 0;

  /** active 卡的落点:站内 route 走 SPA 导航;externalUrl(如漫剧工坊)新开标签页。 */
  const openOf = (pres: NonNullable<ReturnType<typeof presentationOf>>) => {
    if (pres.route) return () => navigate(pres.route!);
    if (pres.externalUrl) return () => openExternalCabin(pres.externalUrl!);
    return undefined;
  };

  /**
   * 一张能力卡该画成**富卡**还是轻量卡。
   * ★只有「render=external 且 active 且真有 externalUrl」才升富卡:富卡整张可点,
   *   占位态(coming_soon / integrating)升上去就成了一张点不动的大卡 —— 假承诺。
   *   后端把它翻回 coming_soon 时,这张卡自动退回轻量占位卡,不用改代码。
   */
  const isRich = (c: CapabilityCardData): boolean => {
    const pres = presentationOf(c.capability);
    return !!pres && pres.render === "external" && c.status === "active" && !!pres.externalUrl;
  };
  const richCaps = caps.filter(isRich);
  const liteCaps = caps.filter((c) => !isRich(c));

  /** 富卡网格(dept 卡 + 外链舱富卡);其一存在就开这面墙。 */
  const hasRichWall = depts.length + richCaps.length > 0;

  const renderLite = (c: CapabilityCardData) => {
    const pres = presentationOf(c.capability);
    if (!pres) return null;
    const active = c.status === "active";
    return (
      <CapabilityCard
        key={c.capability}
        card={c}
        pres={pres}
        onOpen={active ? openOf(pres) : undefined}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* 群页页头 */}
      <header>
        <h1 className="text-xl font-bold tracking-tight">{t(meta.labelKey)}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t(meta.subKey)}
          {countParts.length > 0 && <span className="ml-2">· {countParts.join(" · ")}</span>}
        </p>
      </header>

      {/* 富卡墙(内容群)—— 大卡,走 dept-grid。
          ★该群的能力卡(如漫剧工坊)**并进同一个网格补位**(0901 老板走查:
          5 张 dept 宽屏排 3+2 空一格,能力卡另起一段却孤在新行第一列 —— 第 6 格
          本该补满屏幕)。混排高度由 grid 拉齐,占位卡内容少一点是「即将」应有的样子。
          ★2026-09-20:外链舱(漫剧工坊)在这面墙上与 dept 卡走**同一个外壳**
          (WorkbenchCabinShell),不再是轻量卡 —— 老板「还没有跟其他的统一」那条。 */}
      {hasRichWall && (
        <div className="cardwall">
          <div className="dept-grid">
            {depts.map((d, i) => (
              <WorkbenchDeptCard
                key={d.moduleKey}
                dept={d}
                index={i}
                onOpen={() => navigate(d.route)}
              />
            ))}
            {richCaps.map((c, i) => {
              const pres = presentationOf(c.capability)!;
              return (
                <WorkbenchExternalCabinCard
                  key={c.capability}
                  card={c}
                  pres={pres}
                  index={depts.length + i}
                  onOpen={() => openExternalCabin(pres.externalUrl!)}
                />
              );
            })}
            {liteCaps.map(renderLite)}
          </div>
        </div>
      )}

      {/* 能力舱卡 —— 无富卡的群照旧紧凑 cardwall-cap(250~365) */}
      {!hasRichWall && liteCaps.length > 0 && (
        <div className="cardwall">
          <div className="cardwall-cap">{liteCaps.map(renderLite)}</div>
        </div>
      )}

      {isEmpty && (
        <p className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">
          {t("empty.noData")}
        </p>
      )}

      {/* 该群在途 / 最近(仅有产物线的内容群) */}
      {workLines.length > 0 && (
        <>
          <InFlightRow lines={workLines} onOpen={(w) => navigate(w.detailRoute)} />
          <RecentWorksRow
            lines={workLines}
            onOpen={(w) => navigate(w.detailRoute)}
            onViewAll={(href) => navigate(href)}
          />
        </>
      )}
    </div>
  );
}
